import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { MapPin, HandWaving, UserPlus, ArrowRight } from "@phosphor-icons/react";

/**
 * Live course widget — Dashboard.
 * Shows the viewer's current course + friends on it. Friends only,
 * same-course only. Hidden entirely when the viewer isn't on a course.
 */
export default function LiveCourseWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null); // user_id being sent

  useEffect(() => {
    let live = true;
    const fetchIt = () => {
      api.get("/live/course")
        .then((r) => { if (live) { setData(r.data); setLoading(false); } })
        .catch(() => { if (live) { setData(null); setLoading(false); } });
    };
    fetchIt();
    const t = setInterval(fetchIt, 90 * 1000);  // refresh every 90s
    return () => { live = false; clearInterval(t); };
  }, []);

  if (loading || !data?.current_course) return null;

  const { current_course: course, friends_on_course: friends } = data;

  const sendBula = async (f) => {
    setSending(f.id);
    try {
      await api.post(`/live/bula/${f.id}`);
      toast.success(`Bula sent to ${f.name}! 👋`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't send Bula");
    } finally {
      setSending(null);
    }
  };

  return (
    <div className="card-default p-6 rise" data-testid="live-course-widget">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <span className="label-eyebrow text-[#E05D3A]">● Live now</span>
          <h2 className="font-heading font-bold text-xl mt-1 flex items-center gap-2">
            <MapPin weight="fill" className="text-[#E05D3A]" size={18} />
            On the course at {course.course_name}
          </h2>
          <p className="text-xs text-[#0F2537]/60 mt-1">
            {course.source === "booking"
              ? `Your tee-time was at ${course.since}.`
              : "Your round is in progress."}
            {" "}Showing friends here right now.
          </p>
        </div>
      </div>

      {friends.length === 0 ? (
        <div className="mt-4 p-4 rounded-xl border border-dashed border-[#E2E0D8] text-center text-sm text-[#0F2537]/60" data-testid="live-empty">
          You're flying solo right now. <Link to="/friends" className="text-[#E05D3A] font-semibold">Add friends</Link> to see them here when you cross paths.
        </div>
      ) : (
        <ul className="mt-4 space-y-2" data-testid="live-friends-list">
          {friends.map((f) => (
            <li key={f.id} className="p-3 rounded-xl border border-[#E2E0D8] flex items-center gap-3" data-testid={`live-friend-${f.id}`}>
              <div className="w-10 h-10 rounded-full bg-[#0F2537] text-white flex items-center justify-center font-bold text-sm shrink-0">
                {(f.name || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-sm truncate">{f.name}</div>
                <div className="text-[11px] text-[#0F2537]/60 truncate">
                  {f.source === "booking" ? "Tee time" : "Round in progress"}
                  {f.handicap_index != null && ` · HCP ${f.handicap_index.toFixed(1)}`}
                </div>
              </div>
              <button
                onClick={() => sendBula(f)}
                disabled={sending === f.id}
                data-testid={`bula-btn-${f.id}`}
                className="px-3 py-2 rounded-lg bg-[#E05D3A] text-white text-xs font-bold inline-flex items-center gap-1 hover:brightness-110 disabled:opacity-60"
              >
                <HandWaving size={14} weight="bold" /> {sending === f.id ? "Sending…" : "Bula"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
